/**
 * 「브랜드」 — 내 브랜드·소속자·초대 (261013-account-brand-membership). 대행사 콘솔의 고객 목록·고객 상세를 합쳤다.
 *
 *  - `/app/brands`      내 브랜드 전부(역할·상태·소속자 수)와 브랜드 추가(승인 대기로 들어간다)
 *  - `/app/brands/$id`  링크 고치기 · 계정 관리(소속자와 역할 · 초대는 소유자) · 나가기 · 승인 전 삭제(소유자)
 *
 * ★ 화면은 편의다 — 소유자 전용(초대·내보내기·역할·삭제)과 마지막 소유자 규칙은 **서버가** 막는다(403·409).
 * ★ 브랜드명·질문은 운영자가 정한다 — 여기서는 읽기만 한다.
 */
import { brand } from '@geo-admin/shared/contract/api/index.ts';
import type { BrandRole } from '@geo-admin/shared/contract/api/auth.ts';
import type { MyBrand } from '@geo-admin/shared/contract/api/brand.ts';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import {
  emptyLinks,
  LinksField,
  type LinksValue,
  linksFrom,
  linksPayload,
  linksProblem,
} from '../components/LinksField.tsx';
import { PageTitle } from '../components/PageTitle.tsx';
import { PartnerCard } from '../components/PartnerCard.tsx';
import { Button } from '../components/ui/button.tsx';
import { Badge, Card, CardBody, CardHeader } from '../components/ui/card.tsx';
import { FieldError, Input, Label, Select, Textarea } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi, useApiMutation } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';

export const ROLE_WORD: Record<BrandRole, string> = { owner: '소유자', manager: '관리자' };

export function StatusBadge({ status }: { status: MyBrand['status'] }) {
  if (status === 'pending') return <Badge tone="amber">승인 대기</Badge>;
  if (status === 'confirmed') return <Badge tone="blue">결제 대기</Badge>;
  return <Badge tone="green">이용 중</Badge>;
}

// ── 목록 ─────────────────────────────────────────────────────────────────

export function MyBrandsPage() {
  const brands = useApi(brand.listMyBrands);
  const [adding, setAdding] = useState(false);
  if (brands.isLoading) return <Loading />;
  if (brands.error) return <ErrorBox error={brands.error} />;
  const list = brands.data!.brands;

  return (
    <>
      <PageTitle
        title="브랜드"
        actions={
          <Button variant="live" onClick={() => setAdding((v) => !v)}>
            브랜드 추가
          </Button>
        }
      />
      {adding ? <AddBrandCard onDone={() => setAdding(false)} /> : null}
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-slate-100 border-b text-left text-slate-500 text-xs">
            <tr>
              <th className="px-4 py-2 font-medium">브랜드</th>
              <th className="px-4 py-2 font-medium">상태</th>
              <th className="px-4 py-2 font-medium">내 역할</th>
              <th className="px-4 py-2 text-right font-medium">함께 관리</th>
            </tr>
          </thead>
          <tbody>
            {list.map((b) => (
              <tr key={b.id} className="border-slate-100 border-b last:border-0 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">
                  <Link to="/app/brands/$id" params={{ id: b.id }} className="hover:underline">
                    {b.name}
                  </Link>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={b.status} />
                </td>
                <td className="px-4 py-3 text-slate-600">{ROLE_WORD[b.role]}</td>
                <td className="px-4 py-3 text-right text-slate-600 tabular-nums">{b.memberCount}명</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

export function AddBrandCard({ onDone }: { onDone: () => void }) {
  const add = useApiMutation(brand.addMyBrand);
  // 원본 자산 링크 — 하나 이상 필수 (261010-agency-console)
  const [links, setLinks] = useState<LinksValue>(emptyLinks);
  const [tried, setTried] = useState(false);
  const [desiredQuery, setDesiredQuery] = useState('');
  // 브랜드명 = 표시 이름. 업체명은 계정에 이미 있어 묻지 않는다 (261013-signup-company-name-to-account)
  const [brandName, setBrandName] = useState('');
  const [note, setNote] = useState('');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (linksProblem(links) || desiredQuery.trim().length < 2 || !brandName.trim()) return;
    add.mutate(
      {
        body: {
          brandName: brandName.trim(),
          ...linksPayload(links),
          desiredQuery: desiredQuery.trim(),
          note: note.trim(),
        },
      },
      { onSuccess: onDone },
    );
  };
  return (
    <Card>
      <CardHeader title="브랜드 추가" description="운영팀이 브랜드명과 질문을 등록해 승인하면 측정이 시작됩니다" />
      <CardBody>
        <form className="grid gap-3" onSubmit={submit}>
          {/* 가입 화면 「브랜드 정보」와 같은 순서 — 브랜드명 · 링크 · 원하는 질문 (261013-signup-company-name-to-account) */}
          <Label>
            <span>
              브랜드명 <span className="text-bad">*</span>{' '}
              <span className="font-normal text-slate-500 text-xs">AI 답변에 노출되기 원하는 브랜드의 이름</span>
            </span>
            <Input
              placeholder="예: 하남본치과"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              maxLength={120}
            />
            {tried && !brandName.trim() ? <FieldError>브랜드명을 넣어 주세요</FieldError> : null}
          </Label>
          <LinksField value={links} onChange={setLinks} showProblem={tried} />
          <Label>
            <span>
              원하는 질문 <span className="text-bad">*</span>{' '}
              <span className="font-normal text-slate-500 text-xs">
                AI 에 이 질문을 물었을 때 답변에 위 브랜드가 나와야 합니다. 운영팀이 확인해 확정합니다
              </span>
            </span>
            <Input
              placeholder="예: 강남 임플란트 치과 추천"
              value={desiredQuery}
              onChange={(e) => setDesiredQuery(e.target.value)}
              maxLength={300}
            />
            {tried && desiredQuery.trim().length < 2 ? <FieldError>원하는 질문을 넣어 주세요</FieldError> : null}
          </Label>
          <Label>
            운영팀에 남길 말 (선택)
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
          </Label>
          <FieldError>{add.error?.message}</FieldError>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onDone}>
              취소
            </Button>
            <Button type="submit" variant="live" disabled={add.isPending}>
              추가
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

// ── 한 곳 ────────────────────────────────────────────────────────────────

export function MyBrandDetailPage() {
  const { id } = useParams({ from: '/app/brands/$id' });
  const b = useApi(brand.getMyBrand, { params: { id } });
  const navigate = useNavigate();
  if (b.isLoading) return <Loading />;
  if (b.error) return <ErrorBox error={b.error} />;
  const c = b.data!.brand;
  const owner = c.role === 'owner';

  return (
    <>
      <PageTitle
        title={c.name}
        actions={
          c.status === 'active' ? (
            <Button variant="live" onClick={() => void navigate({ to: '/app/report', search: { brand: c.id } })}>
              보고서 보기
            </Button>
          ) : null
        }
      />
      <div className="flex flex-wrap gap-2">
        <StatusBadge status={c.status} />
        <Badge tone="slate">내 역할: {ROLE_WORD[c.role]}</Badge>
      </div>
      {c.status === 'pending' ? (
        <Card>
          <CardBody className="space-y-2 text-sm">
            <p className="text-slate-700">
              운영팀이 확인하고 있습니다. 브랜드명과 질문을 등록해 승인하면 측정이 시작됩니다.
            </p>
            <div>
              <div className="text-slate-500 text-xs">요청한 질문</div>
              <ul className="list-disc pl-5">
                {c.desiredQueries.map((q) => (
                  <li key={q}>{q}</li>
                ))}
              </ul>
            </div>
            {c.note ? <p className="whitespace-pre-wrap text-slate-600">{c.note}</p> : null}
          </CardBody>
        </Card>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-2">
        <LinksCard brand={c} />
        <MembersCard brand={c} />
        {owner && c.status === 'active' ? <PartnerCard brandId={c.id} /> : null}
        {owner && c.status === 'pending' ? <DeleteCard brand={c} /> : null}
      </div>
    </>
  );
}

function LinksCard({ brand: c }: { brand: MyBrand }) {
  const update = useApiMutation(brand.updateMyBrand);
  const [name, setName] = useState(c.name);
  // 원본 자산 링크 — 고친 뒤에도 하나 이상 남아야 한다(서버도 본다)
  const [links, setLinks] = useState<LinksValue>(() => linksFrom(c));
  const [tried, setTried] = useState(false);
  const [saved, setSaved] = useState(false);
  const payload = linksPayload(links);
  const dirty =
    name.trim() !== c.name ||
    payload.homepageUrl !== c.homepageUrl ||
    payload.naverPlaceUrl !== c.naverPlaceUrl ||
    JSON.stringify(payload.links) !== JSON.stringify(c.links);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setSaved(false);
    setTried(true);
    if (linksProblem(links)) return;
    update.mutate(
      { params: { id: c.id }, body: { name: name.trim(), ...payload } },
      { onSuccess: () => setSaved(true) },
    );
  };

  return (
    <Card>
      <CardHeader title="업체 정보" />
      <CardBody>
        <form className="grid gap-3" onSubmit={submit}>
          <Label>
            브랜드명 (보고서에 보이는 이름)
            <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
          </Label>
          <LinksField value={links} onChange={setLinks} showProblem={tried} />
          <FieldError>{update.error?.message}</FieldError>
          <div className="flex items-center justify-end gap-3">
            {saved && !dirty ? <span className="text-slate-500 text-sm">저장했습니다</span> : null}
            <Button type="submit" disabled={!dirty || update.isPending || !name.trim()}>
              저장
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

/**
 * 계정 관리 — 소속자와 초대를 한 카드에서. 소유자는 역할을 바꾸고 내보내고 초대한다. 누구나 자기 소속에서 나간다.
 * 마지막 소유자는 서버가 막는다(409)
 */
function MembersCard({ brand: c }: { brand: MyBrand }) {
  const navigate = useNavigate();
  const members = useApi(brand.listBrandMembers, { params: { id: c.id } });
  const setRole = useApiMutation(brand.updateBrandMember);
  const remove = useApiMutation(brand.removeBrandMember);
  const owner = c.role === 'owner';
  const err = (setRole.error ?? remove.error)?.message;

  return (
    <Card>
      <CardHeader title="계정 관리" />
      <CardBody className="grid gap-3">
        {members.isLoading ? (
          <Loading />
        ) : members.error ? (
          <ErrorBox error={members.error} />
        ) : (
          <ul className="divide-y divide-slate-100 text-sm">
            {members.data!.members.map((m) => (
              <li key={m.userId} className="flex flex-wrap items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <div className="truncate">
                    {m.name ?? m.email} {m.isMe ? <Badge tone="blue">나</Badge> : null}
                  </div>
                  <div className="truncate text-slate-500 text-xs">
                    {m.email}
                    {m.companyName ? ` · ${m.companyName}` : ''} · {dateTime(m.since)}부터
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {owner ? (
                    <Select
                      className="w-28"
                      value={m.role}
                      disabled={setRole.isPending}
                      onChange={(e) =>
                        setRole.mutate({
                          params: { id: c.id, userId: m.userId },
                          body: { role: e.target.value as BrandRole },
                        })
                      }
                    >
                      <option value="owner">소유자</option>
                      <option value="manager">관리자</option>
                    </Select>
                  ) : (
                    <span className="text-slate-600">{ROLE_WORD[m.role]}</span>
                  )}
                  {/* 소유자는 나가지 않는다 — 나가려면 다른 소유자가 역할을 관리자로 바꾼 뒤다 */}
                  {(m.isMe && m.role !== 'owner') || (!m.isMe && owner) ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0 whitespace-nowrap"
                      disabled={remove.isPending}
                      onClick={() => {
                        const msg = m.isMe
                          ? `${c.name} 에서 나갑니다. 이 브랜드의 보고서를 더 볼 수 없습니다.`
                          : `${m.email} 을(를) ${c.name} 에서 내보냅니다.`;
                        if (!confirm(msg)) return;
                        remove.mutate(
                          { params: { id: c.id, userId: m.userId } },
                          { onSuccess: () => (m.isMe ? void navigate({ to: '/app/brands' }) : undefined) },
                        );
                      }}
                    >
                      {m.isMe ? '나가기' : '내보내기'}
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
        <FieldError>{err}</FieldError>
        {owner ? <InvitesSection brand={c} /> : null}
      </CardBody>
    </Card>
  );
}

/** 초대 — 소유자만(계정 관리 카드 아래쪽). 주소는 메일로만 간다(화면에 링크가 나오지 않는다) */
function InvitesSection({ brand: c }: { brand: MyBrand }) {
  const invites = useApi(brand.listBrandInvites, { params: { id: c.id } });
  const create = useApiMutation(brand.createBrandInvite);
  const revoke = useApiMutation(brand.revokeBrandInvite);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<BrandRole>('manager');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const disabled = c.status !== 'active' && c.status !== 'confirmed';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const to = email.trim();
    create.mutate(
      { params: { id: c.id }, body: { email: to, role } },
      {
        onSuccess: () => {
          setSentTo(to);
          setEmail('');
        },
      },
    );
  };
  const open = (invites.data?.invites ?? []).filter((i) => i.state === 'open');

  return (
    <div className="grid gap-3 border-slate-100 border-t pt-4">
      <div>
        <div className="font-medium text-sm">초대</div>
        <p className="text-slate-500 text-xs">
          메일로 초대합니다. 받은 사람이 계정이 없으면 비밀번호를 정해 만들고, 있으면 그 계정에 이 브랜드가 더해집니다
        </p>
      </div>
      {disabled ? (
        <p className="text-slate-500 text-sm">승인된 뒤 초대할 수 있습니다.</p>
      ) : (
        <form className="flex flex-wrap gap-2" onSubmit={submit}>
          <Input
            type="email"
            placeholder="초대할 이메일"
            className="min-w-0 flex-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Select value={role} onChange={(e) => setRole(e.target.value as BrandRole)} className="w-28">
            <option value="manager">관리자</option>
            <option value="owner">소유자</option>
          </Select>
          <Button type="submit" disabled={create.isPending || !email.trim()}>
            초대
          </Button>
        </form>
      )}
      <FieldError>{(create.error ?? revoke.error)?.message}</FieldError>
      {sentTo ? <p className="text-slate-500 text-sm">{sentTo} 로 초대 메일을 보냈습니다</p> : null}
      {open.length ? (
        <ul className="divide-y divide-slate-100 text-sm">
          {open.map((i) => (
            <li key={i.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <div className="truncate">{i.email}</div>
                <div className="text-slate-500 text-xs">
                  {ROLE_WORD[i.role]} · {dateTime(i.expiresAt)}까지
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate({ params: { id: c.id, inviteId: i.id } })}
              >
                회수
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function DeleteCard({ brand: c }: { brand: MyBrand }) {
  const navigate = useNavigate();
  const del = useApiMutation(brand.deleteMyBrand);
  return (
    <Card>
      <CardHeader title="브랜드 삭제" />
      <CardBody className="grid gap-3 text-sm">
        <p className="text-slate-600">승인 전 신청을 지웁니다. 승인된 브랜드는 운영팀에 요청해 주세요.</p>
        <FieldError>{del.error?.message}</FieldError>
        <div className="flex justify-end">
          <Button
            variant="danger"
            disabled={del.isPending}
            onClick={() =>
              confirm(`${c.name} 신청을 삭제합니다.`) &&
              del.mutate({ params: { id: c.id } }, { onSuccess: () => void navigate({ to: '/app/brands' }) })
            }
          >
            삭제
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
